import { Review } from './../../../api/models/api-models';

export const REVIEWS_PER_PAGE = 20;

export enum ReviewFilters {
  ALL_DIRECTORIES = 'ALL_DIRECTORIES',
  ALL_STATUS = 'ALL_STATUS',
  ALL_RATINGS = 'ALL_RATINGS',
  REPLIED = 'REPLIED',
  NOT_REPLIED = 'NOT_REPLIED',
  HIGHEST_RATED = 'HIGHEST_RATED',
  LOWEST_RATED = 'LOWEST_RATED',
  NEWEST = 'NEWEST',
  OLDEST = 'OLDEST',
  STATUS = 'STATUS',
  ONE = 'ONE',
  TWO = 'TWO',
  THREE = 'THREE',
  FOUR = 'FOUR',
  FIVE = 'FIVE',
  BROWNBOOK = 'BROWNBOOK',
  CYLEX = 'CYLEX',
  FACEBOOK = 'FACEBOOK',
  FIND_OPEN = 'FIND_OPEN',
  FOURSQUARE = 'FOURSQUARE',
  GOOGLE_SEARCH = 'GOOGLE',
  INSTAGRAM = 'INSTAGRAM',
  I_GLOBAL = 'I_GLOBAL',
  INFO_IS_INFO = 'INFO_IS_INFO',
  STADTBRANCHENBUCH = 'STADTBRANCHENBUCH',
  TRIP_ADVISOR = 'TRIP_ADVISOR',
  WHERE_TO = 'WHERE_TO',
}

export enum UberallDirectoriesNames {
  BROWNBOOK = 'Brownbook',
  CYLEX = 'Cylex',
  FACEBOOK = 'Facebook',
  FIND_OPEN = 'Find Open',
  FOURSQUARE = 'Foursquare',
  GOOGLE_SEARCH = 'Google Search',
  INSTAGRAM = 'Instagram',
  I_GLOBAL = 'iGlobal',
  INFO_IS_INFO = 'infoisinfo',
  STADTBRANCHENBUCH = 'Opendi',
  TRIP_ADVISOR = 'Trip Advisor',
  WHERE_TO = 'Where To?',
}

export interface IUberallDirectory {
  filterId: string;
  name: UberallDirectoriesNames;
}

export const UBERALL_DIRECTORIES = [
  { filterId: ReviewFilters.BROWNBOOK, name: UberallDirectoriesNames.BROWNBOOK },
  { filterId: ReviewFilters.CYLEX, name: UberallDirectoriesNames.CYLEX },
  { filterId: ReviewFilters.FACEBOOK, name: UberallDirectoriesNames.FACEBOOK },
  { filterId: ReviewFilters.FIND_OPEN, name: UberallDirectoriesNames.FIND_OPEN },
  { filterId: ReviewFilters.FOURSQUARE, name: UberallDirectoriesNames.FOURSQUARE },
  { filterId: ReviewFilters.GOOGLE_SEARCH, name: UberallDirectoriesNames.GOOGLE_SEARCH },
  { filterId: ReviewFilters.INSTAGRAM, name: UberallDirectoriesNames.INSTAGRAM },
  { filterId: ReviewFilters.I_GLOBAL, name: UberallDirectoriesNames.I_GLOBAL },
  { filterId: ReviewFilters.INFO_IS_INFO, name: UberallDirectoriesNames.INFO_IS_INFO },
  { filterId: ReviewFilters.STADTBRANCHENBUCH, name: UberallDirectoriesNames.STADTBRANCHENBUCH },
  { filterId: ReviewFilters.TRIP_ADVISOR, name: UberallDirectoriesNames.TRIP_ADVISOR },
  { filterId: ReviewFilters.WHERE_TO, name: UberallDirectoriesNames.WHERE_TO },
];

export enum UberallReviewQueryParameters {
  DIRECTORY = '&directoryTypes=',
  RATING = '&ratings=',
  REPLIED = '&replied=',
  PAGE_SIZE = '&pageSize=',
  PAGE = '?page=',
}

/**
 * Sorts reviews from the oldest to the newest or from the newest to the oldest
 * @param recentToOld The type of the sorting
 * @param reviews Reviews to sort
 */
export function sortReviewsByDate(recentToOld: boolean, reviews: Review[]) {
  return reviews.sort((a: Review, b: Review) => {
    const direction = recentToOld ? 1 : -1;
    if (a.createTime === b.createTime) {
      return 0;
    }
    if (b.createTime < a.createTime) {
      return -1 * direction;
    }
    return direction;
  });
}

/**
 * Sorts reviews from the highest rated to the least rated or the other way around
 * @param highestToLeast The type of the sorting
 * @param reviews Reviews to sort
 */
export function sortReviewsByRating(highestToLeast: boolean, reviews: Review[]) {
  return reviews.sort((a: Review, b: Review) => {
    const direction = highestToLeast ? 1 : -1;
    const aRating = transformStarRating(a.starRating);
    const bRating = transformStarRating(b.starRating);

    if (aRating === bRating) {
      return 0;
    }
    if (bRating < aRating) {
      return -1 * direction;
    }
    return direction;
  });
}

/**
 * Transforms start rating string to number. This is needed because of GOOGLE API
 * @param value value to transform
 */
export function transformStarRating(value: string): number {
  switch (value) {
    case 'FIVE':
      return 5;
    case 'FOUR':
      return 4;
    case 'THREE':
      return 3;
    case 'TWO':
      return 2;
    case 'ONE':
      return 1;
    default:
      return 0;
  }
}

/**
 * Filters the review depending on the filter type
 *
 * @param reviews reviews to filter
 * @param filter filter
 */
export function getFilteredBasicReviews(reviews: Review[], filter: ReviewFilters): Review[] {
  switch (filter) {
    case ReviewFilters.REPLIED: {
      return [...reviews].filter((review: Review) => review.reviewReply);
    }
    case ReviewFilters.NOT_REPLIED: {
      return [...reviews].filter((review: Review) => !review.reviewReply);
    }
    case ReviewFilters.LOWEST_RATED: {
      return sortReviewsByRating(false, [...reviews]);
    }
    case ReviewFilters.HIGHEST_RATED: {
      return sortReviewsByRating(true, [...reviews]);
    }
    case ReviewFilters.OLDEST: {
      return sortReviewsByDate(false, [...reviews]);
    }
    case ReviewFilters.NEWEST: {
      return sortReviewsByDate(true, [...reviews]);
    }
    case ReviewFilters.ONE: {
      return [...reviews].filter((review: Review) => review.starRating === filter);
    }
    case ReviewFilters.TWO: {
      return [...reviews].filter((review: Review) => review.starRating === filter);
    }
    case ReviewFilters.THREE: {
      return [...reviews].filter((review: Review) => review.starRating === filter);
    }
    case ReviewFilters.FOUR: {
      return [...reviews].filter((review: Review) => review.starRating === filter);
    }
    case ReviewFilters.FIVE: {
      return [...reviews].filter((review: Review) => review.starRating === filter);
    }
    default: {
      return [...reviews].filter((review: Review) => review.directory === filter);
    }
  }
}
